import { useMemo } from "react";

/**
 * Renders a name with automatic name-case. Legacy IBO records have names in ALL CAPS
 * and modern ones have names in no particular convention at all, so name case has
 * effectively been lost. This component attempts to guess at the correct formatting
 * based on how names are usually written.
 */
export default function FormattedName({ value }) {
  // Formatted name is re-processed only when 'value' changes.
  const formatted = useMemo(() => {
    if (typeof value !== "string") {
      throw new Error(`FormattedName value must be a string`);
    }

    try {
      const normalized = value
        .toLowerCase()
        .trim()
        .split(" ")
        .filter((part) => part !== "");
      const processed = [];

      for (const part of normalized) {
        if (/^o'[a-z]/.test(part)) {
          // O'Malley, O'Brian, et al.
          processed.push(`O'${part[2].toUpperCase()}${part.slice(3)}`);
        } else if (/^d'[a-z]/.test(part)) {
          // D'Angelo, et al.
          processed.push(`D'${part[2].toUpperCase()}${part.slice(3)}`);
        } else if (/^mcc/.test(part)) {
          // McCoy 🤫
          processed.push(`McC${part.slice(3)}`);
        } else if (/[a-z]-[a-z]/.test(part)) {
          // Hyphenated names
          processed.push(
            part[0].toUpperCase() +
              part
                .slice(1)
                .replace(/([a-z])-([a-z])/g, (match, group1, group2) => {
                  return `${group1}-${group2.toUpperCase()}`;
                })
          );
        } else if (["del", "de"].includes(part)) {
          // Lowercased mid-name fragments.
          processed.push(part);
        } else {
          // All others.
          processed.push(part[0].toUpperCase() + part.slice(1));
        }
      }

      return processed.join(" ");
    } catch (err) {
      // If something crashes just return as-is.
      return value;
    }
  }, [value]);

  return <span>{formatted}</span>;
}
